import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { motion } from 'motion/react';

import { createClassNames } from '@hrworks/design-system/functions';
import { mq, shouldForwardProp } from '@hrworks/design-system/utilities';
import type { Typography } from '@hrworks/sui-shared/types';

import { Button } from '../Button';
import { Title as _Title, TitleStyles } from '../Title';
import { Divider as _Divider } from './Divider';
import type { SectionProps } from './Section.types';

const componentConfig = (theme: Theme) => ({
  offset: theme.eclipseTier1.spacing['12px'],
});

const Section = styled.section({
  display: 'grid',
  alignItems: 'center',
});

const SectionToggle = styled(Button, {
  shouldForwardProp,
})<{
  $typography: Typography;
}>(({ theme, $typography }) => {
  const { offset } = componentConfig(theme);

  return {
    zIndex: 3,
    padding: 0,
    justifyContent: 'flex-end',
    borderRadius: `calc(${theme.eclipseTier2Typography[$typography].fontSize} / 2)`,
    width: `calc(100% + 2 * ${offset})`,
    marginLeft: `calc(-1 * ${offset})`,
    paddingRight: offset,
    height: '100%',
    gridArea: '1 / 1 / 2 / 2',
  };
});

const IconWrapper = styled.span<Pick<SectionProps, 'expanded'>>(({ theme, expanded }) => ({
  display: 'flex',
  ...(expanded && {
    transform: 'rotateX(180deg)',
  }),
  [mq.conditionalTransition]: {
    transition: `transform ${theme.marko.variables.animationDuration.normal}`,
  },
}));

const Title = styled(_Title)(({ alignTitle }) => ({
  pointerEvents: 'none',
  marginRight: '2rem',
  zIndex: 3,
  gridArea: '1 / 1 / 2 / 2',
  height: '2em',
  ...(alignTitle === 'center' && {
    [`${TitleStyles.sel.Title}`]: {
      marginLeft: '2rem',
    },
  }),
}));

const MotionDiv = styled(motion.div)({
  overflowY: 'clip',
  overflowX: 'visible',
  '&:has([data-section-hide-overflow])': {
    overflowX: 'hidden',
  },
});

const ChildrenContainer = styled.div(({ theme }) => ({
  marginTop: theme.eclipseTier1.spacing['16px'],
  marginBottom: theme.eclipseTier1.spacing['4px'], // Prevent clipping of outline (e.g. hover/focus) from elements inside
}));

const Divider = styled(_Divider)(({ theme }) => ({
  border: 0,
  margin: 0,
  marginTop: theme.eclipseTier1.spacing['4px'],
  zIndex: 3,
}));

const components = { Section, SectionToggle, IconWrapper, Title, MotionDiv, ChildrenContainer, Divider };

export const S = {
  ...components,
  ...createClassNames('Section', components),
} as const;
